import { getInitials, isDefined } from 'support/helpers/generic/generic';
import { createProductsRecord, OrderData } from 'support/helpers/orderForms/orderForms';
import { IdentifierDomainOptions, OrderFormDTO, OrderFormProductDTO, PartyIdentifierDTO } from 'support/types';

import {
  Canonical,
  CanonicalLineItem,
  CanonicalParty,
  CanonicalPostalAddress,
  CanonicalProductIdentifiersDomain,
} from './canonicalTypes';
import { shouldPayTaxes } from './shouldPayTaxes';

export const transformOrderToCanonicalFormat = (
  orderData: OrderData,
  orderFormData: OrderFormDTO | undefined,
): Canonical => {
  if (!orderData.parties) {
    throw new Error('Order data must include parties information');
  }
  return {
    order_identifier: generateOrderId(orderData.parties?.supplier?.name || '', orderData.parties?.buyer?.name || ''),
    order_date: Math.floor(Date.now() / 1000),
    parties: transformPartiesToCanonical(orderData.parties),
    currency: orderFormData?.data?.currency,
    line_items: transformProductsToLineItems(
      orderFormData?.data,
      orderData.products,
      shouldPayTaxes(orderData.parties),
    ),
    comments: orderData.comments,
  };
};

const findIdentifierByDomain = (
  identifiers: Array<PartyIdentifierDTO> | undefined,
  domain: PartyIdentifierDTO['domain'],
): string | null => {
  return identifiers?.find((id) => id.domain === domain)?.identifier || null;
};

const transformAddressToCanonical = (party: {
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country_code?: string;
}): CanonicalPostalAddress => ({
  street: party.street || null,
  city: party.city || null,
  state: party.state || null,
  postal_code: party.postal_code || null,
  country_code: party.country_code || null,
});

type PartyToCanonical = {
  name?: string;
  identifiers?: Array<PartyIdentifierDTO>;
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country_code?: string;
  contact?: ContactToCanonical;
};

type ContactToCanonical = {
  first_name: string | undefined;
  email: string | undefined;
};

const transformPartyToCanonical = (party: PartyToCanonical): CanonicalParty => ({
  name: party.name,
  identifiers: {
    gln: findIdentifierByDomain(party.identifiers, 'GS1'),
    vat: findIdentifierByDomain(party.identifiers, 'vatID'),
    receiver_internal: findIdentifierByDomain(party.identifiers, 'internal'),
  },
  postal_address: transformAddressToCanonical(party),
  contacts: party.contact
    ? {
        main: {
          first_name: party.contact.first_name,
          email: party.contact.email,
        },
      }
    : undefined,
});

const generateOrderId = (supplierName: string, buyerName: string) => {
  const timestamp = Math.floor(Date.now() / 1000);
  const sanitizedSupplierName = getInitials(supplierName, 0);
  const sanitizedBuyerName = getInitials(buyerName, 0);
  return `ORD-${sanitizedSupplierName}-${sanitizedBuyerName}-${timestamp}`;
};

const transformPartiesToCanonical = (parties: NonNullable<OrderData['parties']>) => ({
  buyer: transformPartyToCanonical(parties.buyer || {}),
  supplier: transformPartyToCanonical(parties.supplier || {}),
  ship_to: transformPartyToCanonical(parties.ship_to || {}),
  bill_to: transformPartyToCanonical(parties.bill_to || {}),
});

const transformProductsToLineItems = (
  orderFormData: OrderFormDTO['data'],
  orderProducts: OrderData['products'],
  includeTaxes: boolean,
) => {
  const productsRecord = createProductsRecord(orderFormData);

  const lineItems = Object.entries(orderProducts)
    .map(([productId, orderFormValue], index) => {
      const product = productsRecord[productId];
      if (product && orderFormValue.quantity > 0) {
        return transformProductToLineItem(product, orderFormValue.quantity, index, includeTaxes);
      }
    })
    .filter(isDefined);
  return lineItems;
};

const transformProductToLineItem = (
  product: OrderFormProductDTO,
  quantity: number,
  index: number,
  includeTaxes: boolean,
): CanonicalLineItem => {
  return {
    order_line_identifier: String(index + 1),
    item: {
      type: 'PRODUCT',
      identifiers: transformProductIdentifiers(product.identifiers),
      name: product.name,
      unit_of_measure: product.unit_of_measure,
      tax_percentage: includeTaxes ? product.tax_rate : 0,
      unit_price: product.unit_price,
    },
    modification_groups:
      product.discount_percentage && product.discounted_unit_price != null
        ? [
            {
              allowances: [
                {
                  type: 'RELATIVE',
                  amount: product.unit_price - product.discounted_unit_price,
                  percentage: product.discount_percentage,
                },
              ],
            },
          ]
        : undefined,
    ordered_quantity: quantity,
  };
};

const transformProductIdentifiers = (
  identifiers: OrderFormProductDTO['identifiers'],
): Record<IdentifierDomainOptions, string> => {
  return identifiers.reduce(
    (acc, identifier) => {
      const domain = identifier.domain;
      switch (domain) {
        case 'RECEIVER_INTERNAL':
          acc[CanonicalProductIdentifiersDomain.SENDER_INTERNAL] = identifier.value;
          break;
        case 'SENDER_INTERNAL':
          acc[CanonicalProductIdentifiersDomain.RECEIVER_INTERNAL] = identifier.value;
          break;
        case 'GTIN':
          acc[CanonicalProductIdentifiersDomain.GTIN] = identifier.value;
          break;
      }
      return acc;
    },
    {} as Record<string, string>,
  );
};
